import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import { useTheme } from "@mui/material/styles";
import { TeamUsersAmount } from "components/TeamUsersAmount";
import useTeam, { getTeamBoost, restartTeamStats } from "hooks/useTeam";
import { nanoid } from "nanoid";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ButtonArchiveTeam from "../components/ArchiveTeamButton";
import OfficialStatusButton from "../components/OfficialStatusButton";
import OfficialBadge from "../components/OfficialBadge";
import { TeamIcon } from "../components/brandlibrary/MingleIcons";
import ButtonBoosterContainer from "../components/ButtonBoosterContainer";
import ButtonBoostTeam from "../components/ButtonBoostTeam";
import ButtonLeague from "../components/ButtonLeague";
import ButtonOrganization from "../components/ButtonOrganization";
import DialogDeleteTeam from "../components/DialogDeleteTeam";
import useOpponents from "../hooks/useOpponents";
import {
  IBoostStatus,
  ITeamMember,
  MemberRoleEnum,
} from "../utils/api.interfaces";

export default function Team() {
  const { teamId } = useParams();
  const { dataOpponents } = useOpponents(teamId);
  const theme = useTheme();
  const navigate = useNavigate();
  const { dataTeamIsLoading, dataTeam, refetch } = useTeam(teamId);

  const [boostStatus, setBoostStatus] = React.useState<IBoostStatus | null>(
    null
  );
  const [boostUsersIds, setBoostUsersIds] = React.useState<string[] | null>(
    null
  );
  const [showDeleteDialog, setShowDeleteDialog] =
    React.useState<boolean>(false);
  const [isRestartingStats, setIsRestartingStats] =
    React.useState<boolean>(false);

  const handleRestartStats = async () => {
    if (!teamId) return;
    if (
      !window.confirm(
        "Are you sure you want to restart the stats for this team?"
      )
    )
      return;
    setIsRestartingStats(true);
    try {
      await restartTeamStats(teamId);
      alert("Stats restarted successfully");
    } catch {
      alert("Failed to restart stats");
    } finally {
      setIsRestartingStats(false);
    }
  };

  const teamUrl =
    process.env.REACT_APP_ENV === "PROD"
      ? "https://app.mingle.sport/public/team"
      : "https://develop.app-mingle-sport.pages.dev/public/team";

  React.useEffect(() => {
    if (teamId) {
      getTeamBoost(teamId).then((res) => setBoostStatus(res.boostStatus));
    }
  }, [teamId, dataTeam]);

  React.useEffect(() => {
    const boostUsersId = [
      ...new Set(
        boostStatus?.teamBoosters.map((booster) => booster.userId) || []
      ),
    ];
    setBoostUsersIds(boostUsersId);
  }, [boostStatus]);

  const renderTeamInfo = () => (
    <TableContainer>
      <Table aria-label="team details">
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row" colSpan={4}>
              <Typography
                variant="h1"
                color="text.secondary"
                align="center"
                gutterBottom
              >
                {dataTeam?.name}{" "}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="text.secondary"
                align="center"
              >
                ({dataTeam?.members.length} members)
              </Typography>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row">
              Player, trainer, support:
            </TableCell>

            <TeamUsersAmount
              players={dataTeam?.members?.filter((member) =>
                member.rolesData.some(
                  (role) => role.roleType === MemberRoleEnum.player
                )
              )}
              trainers={dataTeam?.members?.filter((member) =>
                member.rolesData.some(
                  (role) =>
                    role.roleType === MemberRoleEnum.trainer ||
                    role.roleType === MemberRoleEnum.assistant ||
                    role.roleType === MemberRoleEnum.manager ||
                    role.roleType === MemberRoleEnum.coach
                )
              )}
              support={dataTeam?.members?.filter((member) =>
                member.rolesData.some(
                  (role) =>
                    role.roleType === MemberRoleEnum.support ||
                    role.roleType === MemberRoleEnum.parent
                )
              )}
            />
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Name Abbreviation:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              {dataTeam?.nameAbbreviation}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Invite Code:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              {dataTeam?.inviteCode}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Sport Type:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              {dataTeam?.sportType}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Team ID:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <Button
                component="a"
                target="_blank"
                size="small"
                variant="text"
                color="info"
                href={`${teamUrl}/${dataTeam?.id}`}
                sx={{ whiteSpace: "nowrap" }}
                startIcon={<>⚽</>}
                endIcon={<ChevronRightIcon />}
              >
                Public team page
              </Button>
              {dataTeam?.id}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              League:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <ButtonLeague leagueId={dataTeam?.league?.id} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Organization:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <ButtonOrganization organizationId={dataTeam?.organization?.id} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Opponents:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {dataOpponents?.opponents.map((opponent) => (
                  <Button
                    key={nanoid(6)}
                    color="secondary"
                    sx={{
                      cursor: "pointer",
                      fontSize: "12px",
                      height: "42px",
                      pointerEvents: "none",
                      whiteSpace: "nowrap",
                    }}
                    size="small"
                    startIcon={
                      <Avatar
                        sx={{
                          backgroundColor: "#fff",
                          marginRight: "6px",
                          width: "35px",
                          height: "35px",
                        }}
                        src={opponent.logoUrl}
                      />
                    }
                    variant="outlined"
                  >
                    {opponent.name}
                  </Button>
                ))}
              </Stack>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Boost Team:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <Stack
                spacing={6}
                direction="row"
                justifyContent="end"
                alignItems="center"
              >
                {dataTeam?.id && (
                  <ButtonBoostTeam
                    teamId={dataTeam.id}
                    refetch={() => {
                      if (teamId)
                        getTeamBoost(teamId).then((res) =>
                          setBoostStatus(res.boostStatus)
                        );
                      return refetch();
                    }}
                  />
                )}
              </Stack>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Team Boost Level:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <Typography variant="body2">{boostStatus?.boostLevel}</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Team Boosters:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <Stack
                spacing={6}
                direction="row"
                justifyContent="end"
                alignItems="center"
              >
                {dataTeam?.id &&
                  boostUsersIds?.map((userId) => (
                    <ButtonBoosterContainer
                      key={nanoid(6)}
                      userId={userId}
                      teamId={dataTeam.id}
                      refetch={refetch}
                    />
                  ))}
              </Stack>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Is Archived:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <Stack
                spacing={6}
                direction="row"
                justifyContent="end"
                alignItems="center"
              >
                <Typography variant="body2">
                  {dataTeam?.isArchived?.toString()}
                </Typography>
                <ButtonArchiveTeam team={dataTeam} refetch={refetch} />
              </Stack>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Is Official:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              <Stack
                spacing={6}
                direction="row"
                justifyContent="end"
                alignItems="center"
              >
                {dataTeam?.isOfficial ? (
                  <OfficialBadge />
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    Not official
                  </Typography>
                )}
                <OfficialStatusButton team={dataTeam} refetch={refetch} />
              </Stack>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Is Ghost:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              {dataTeam?.isGhost?.toString()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Is Historic:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              {dataTeam?.isHistoric?.toString()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Seasons:
            </TableCell>
            <TableCell align="right" colSpan={3}>
              {dataTeam?.season && (
                <div>
                  <strong>{dataTeam?.season.name}</strong>
                  <br />
                  {new Date(dataTeam?.season.startsAtUtc).toLocaleDateString(
                    [],
                    {
                      weekday: "short",
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                    }
                  )}
                  -
                  {new Date(dataTeam?.season.endsAtUtc).toLocaleDateString([], {
                    weekday: "short",
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                  })}
                </div>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMemberRow = (member: ITeamMember) => (
    <TableRow
      key={member.id}
      sx={{
        "&:nth-of-type(odd)": { backgroundColor: "action.hover" },
        "&:hover": { backgroundColor: "action.selected" },
        transition: "background-color 0.2s",
      }}
    >
      <TableCell sx={{ py: 1 }}>
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          component={member.ownerId ? Link : "div"}
          to={member.ownerId ? `/users/${member.ownerId}` : "#"}
          sx={{
            cursor: member.ownerId ? "pointer" : "default",
            color: member.ownerId ? "#000" : "gray",
          }}
        >
          <Avatar
            alt={`${member.firstName} ${member.lastName}`}
            src={member.imageUrl || member.profileImageUrl}
            sx={{ width: 20, height: 20 }}
          />
          <ListItemText
            primary={`${member.firstName} ${member.lastName}`}
            secondary={member.email || "no known email"}
            sx={{
              "& .MuiTypography-root": { fontSize: "0.875rem" },
              "& .MuiTypography-body2": { fontSize: "0.75rem" },
            }}
          />
        </Stack>
      </TableCell>
      <TableCell align="center" sx={{ py: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          justifyContent="center"
        >
          {member.rolesData.map((role) => (
            <Chip
              key={role.roleType}
              label={role.roleType}
              size="small"
              sx={{ fontSize: "0.75rem" }}
            />
          ))}
        </Stack>
      </TableCell>
      <TableCell align="center" sx={{ py: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          justifyContent="center"
        >
          {member.teamManagementRoles?.map((role) => (
            <Chip
              key={role}
              label={role}
              size="small"
              color="primary"
              sx={{ fontSize: "0.75rem" }}
            />
          ))}
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Typography
        component="h1"
        variant="h1"
        color="inherit"
        noWrap
        gutterBottom
        sx={{
          textAlign: "center",
          margin: "40px 0px",
        }}
      >
        {`Team: ${dataTeam?.organization?.name} - ${dataTeam?.name}`}
      </Typography>

      <Stack spacing={3} sx={{ maxWidth: "1150px", margin: "auto" }}>
        <Card>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Button
                component={Link}
                size="small"
                variant="contained"
                color="success"
                to="/teams/add/"
                startIcon={<TeamIcon />}
              >
                Add new team
              </Button>
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={handleRestartStats}
                  disabled={isRestartingStats}
                  startIcon={<RestartAltIcon />}
                >
                  {isRestartingStats ? "Restarting..." : "Restart Stats"}
                </Button>
                <Button
                  component={Link}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  to={`/teams/edit/${dataTeam?.id || dataTeam?.teamId}`}
                  startIcon={<EditIcon />}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setShowDeleteDialog(true)}
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>

            <Avatar sx={{ width: 175, height: 175, margin: "20px auto" }}>
              <img
                src={dataTeam?.logoUrl}
                style={{ width: "140px", height: "auto" }}
                alt="Team logo"
              />
            </Avatar>

            {renderTeamInfo()}

            {dataTeam && (
              <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 1 }}>
                        <Typography
                          variant="h1"
                          sx={{
                            fontWeight: "700",
                            marginBottom: "8px",
                            fontSize: "18px",
                            textAlign: "center",
                          }}
                        >
                          Members & Roles & Permissions
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 1 }}>Member</TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        Roles
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        Management Roles
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataTeam.members
                      .sort((a, b) => {
                        if (a.ownerId && !b.ownerId) return -1;
                        if (!a.ownerId && b.ownerId) return 1;
                        return `${a.firstName} ${a.lastName}`.localeCompare(
                          `${b.firstName} ${b.lastName}`
                        );
                      })
                      .map(renderMemberRow)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>

          <CardActions>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ width: "100%", padding: 2 }}
            >
              <Button
                component={Link}
                size="small"
                variant="contained"
                color="success"
                to="/teams/add/"
                startIcon={<TeamIcon />}
              >
                Add new team
              </Button>
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={handleRestartStats}
                  disabled={isRestartingStats}
                  startIcon={<RestartAltIcon />}
                >
                  {isRestartingStats ? "Restarting..." : "Restart Stats"}
                </Button>
                <Button
                  component={Link}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  to={`/teams/edit/${dataTeam?.id}`}
                  startIcon={<EditIcon />}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setShowDeleteDialog(true)}
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          </CardActions>
        </Card>

        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate(-1)}
          startIcon={<ChevronLeftIcon />}
        >
          Back
        </Button>
      </Stack>
      <DialogDeleteTeam
        id={teamId}
        isOpen={showDeleteDialog}
        onIsOpenChange={setShowDeleteDialog}
        navigateOnDelete="/teams"
      />
    </>
  );
}
